import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { StatePageVGDisclaimerModal } from '../../components/StatePageVGDisclaimerModal/StatePageVGDisclaimerModal';

interface VGDisclaimerModal {
  name: 'vgDisclaimer';
}

type StatePageModal = VGDisclaimerModal | null;

interface StatePageModalContext {
  displayModal: (x: StatePageModal) => void;
}

export const StatePageModalContext = createContext<StatePageModalContext | undefined>(undefined);

export const useStatePageModalContext = (): StatePageModalContext => {
  const context = useContext(StatePageModalContext);
  if (context === undefined) {
    throw new Error('StatePageModalContext must be used within a StatePageModalContext.Provider');
  }
  return context;
};

interface StatePageModalHandlerProps {
  modal: StatePageModal;
  onClose: () => void;
}

export const StatePageModalHandler: FunctionComponent<StatePageModalHandlerProps> = ({
  modal,
  onClose,
}) => {
  switch (modal?.name) {
    case 'vgDisclaimer':
      return <StatePageVGDisclaimerModal opened onClose={onClose} />;
    default:
      return null;
  }
};

export const StatePageModalContextProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const [modal, setModal] = useState<StatePageModal>(null);
  const contextValue = useMemo(
    () => ({
      displayModal: (a: StatePageModal) => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setModal(a);
          }, 0);
        });
      },
    }),
    [setModal],
  );

  return (
    <StatePageModalContext.Provider value={contextValue}>
      {modal && <StatePageModalHandler modal={modal} onClose={() => setModal(null)} />}
      {children}
    </StatePageModalContext.Provider>
  );
};
